import React, { useEffect, useMemo, useState, useCallback } from "react"
import { Link, useLocation } from "react-router-dom"
import Back from "../common/Back"
import ListingCard from "./ListingCard"
import SignInPrompt from "../common/SignInPrompt"
import img from "../images/about.jpg"
import { API_BASE, authFetch, buildQuery } from "../../lib/api"
import { useAuth } from "../../context/AuthContext"
import "./listings.css"

const CATEGORY_OPTIONS = [
  { value: "", label: "All listings" },
  { value: "For Sale", label: "For Sale" },
  { value: "For Rent", label: "For Rent" },
]

const SORT_OPTIONS = [
  { value: "default", label: "Default" },
  { value: "price-asc", label: "Price: low to high" },
  { value: "price-desc", label: "Price: high to low" },
]

const DEFAULT_TYPE_OPTIONS = [
  "Apartment",
  "Condos",
  "Offices",
  "Homes & Villas",
  "Commercial",
]

const Listings = () => {
  const location = useLocation()
  const { user } = useAuth()
  const [locationFilter, setLocationFilter] = useState("")
  const [category, setCategory] = useState("")
  const [propertyType, setPropertyType] = useState("")
  const [priceMin, setPriceMin] = useState("")
  const [priceMax, setPriceMax] = useState("")
  const [sort, setSort] = useState("default")

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.has("location")) setLocationFilter(params.get("location") || "")
    if (params.has("category")) setCategory(params.get("category") || "")
    if (params.has("type")) setPropertyType(params.get("type") || "")
    if (params.has("priceMin")) setPriceMin(params.get("priceMin") || "")
    if (params.has("priceMax")) setPriceMax(params.get("priceMax") || "")
    if (params.has("sort")) setSort(params.get("sort") || "default")
  }, [location.search])
  const [listings, setListings] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [savedListingIds, setSavedListingIds] = useState([])
  const [saveSearchLoading, setSaveSearchLoading] = useState(false)
  const [saveSearchSuccess, setSaveSearchSuccess] = useState(false)
  const [showSignInPrompt, setShowSignInPrompt] = useState(false)
  const [signInPromptMessage, setSignInPromptMessage] = useState("Sign in to continue.")

  const queryParams = useMemo(
    () => ({
      location: locationFilter || undefined,
      category: category || undefined,
      type: propertyType || undefined,
      priceMin: priceMin || undefined,
      priceMax: priceMax || undefined,
      sort: sort !== "default" ? sort : undefined,
      limit: 100,
    }),
    [locationFilter, category, propertyType, priceMin, priceMax, sort]
  )

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError("")
    const url = `${API_BASE}/api/listings${buildQuery(queryParams)}`
    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return
        if (data.error) {
          setError(data.error)
          setListings([])
          setTotal(0)
        } else {
          setListings(data.listings || [])
          setTotal(data.total != null ? data.total : (data.listings || []).length)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError("Failed to load listings")
          setListings([])
          setTotal(0)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [queryParams])

  const propertyTypeOptions = useMemo(() => {
    const types = [...new Set(listings.map((item) => item.type).filter(Boolean))]
    const options = types.length ? types : DEFAULT_TYPE_OPTIONS
    return [
      { value: "", label: "All types" },
      ...options.map((t) => ({ value: t, label: t })),
    ]
  }, [listings])

  useEffect(() => {
    if (!user) {
      setSavedListingIds([])
      return
    }
    authFetch("/api/saved-homes")
      .then((r) => r.json())
      .then((data) => {
        if (data.savedHomes) setSavedListingIds(data.savedHomes.map((h) => h.listingId))
      })
      .catch(() => {})
  }, [user])

  const handleSaveSearch = useCallback(async () => {
    if (!user) {
      setSignInPromptMessage("Sign in to save this search to your dashboard.")
      setShowSignInPrompt(true)
      return
    }
    setSaveSearchLoading(true)
    setSaveSearchSuccess(false)
    try {
      const res = await authFetch("/api/saved-searches", {
        method: "POST",
        body: JSON.stringify({
          location: queryParams.location || undefined,
          type: queryParams.type || undefined,
          category: queryParams.category || undefined,
          priceMin: queryParams.priceMin != null ? Number(queryParams.priceMin) : undefined,
          priceMax: queryParams.priceMax != null ? Number(queryParams.priceMax) : undefined,
        }),
      })
      if (res.ok) setSaveSearchSuccess(true)
    } finally {
      setSaveSearchLoading(false)
    }
  }, [user, queryParams])

  const handleToggleSaveListing = useCallback(
    (listingId) => {
      if (!user) {
        setSignInPromptMessage("Sign in to save homes to My List.")
        setShowSignInPrompt(true)
        return
      }
      const isSaved = savedListingIds.includes(listingId)
      if (isSaved) {
        authFetch(`/api/saved-homes/${listingId}`, { method: "DELETE" }).then((r) => {
          if (r.ok) setSavedListingIds((prev) => prev.filter((id) => id !== listingId))
        })
      } else {
        authFetch("/api/saved-homes", {
          method: "POST",
          body: JSON.stringify({ listingId }),
        }).then((r) => r.json()).then((data) => {
          if (data.listingId) setSavedListingIds((prev) => (prev.includes(data.listingId) ? prev : [...prev, data.listingId]))
        })
      }
    },
    [user, savedListingIds]
  )

  const count = total

  return (
    <div className='listings-page'>
      <section className='mb'>
        <Back name='Listings' title='Browse All Properties' cover={img} />
        <div className='container'>
          <div className='listings-toolbar'>
            <div className='listings-results'>
              {loading ? (
                "Loading…"
              ) : (
                <>
                  <strong>{count}</strong>{" "}
                  {count === 1 ? "property" : "properties"} found
                </>
              )}
            </div>
            <div className='listings-filters'>
              <div className='filter-group' style={{ alignSelf: "flex-end" }}>
                <button
                  type='button'
                  className='listings-save-search-btn'
                  onClick={handleSaveSearch}
                  disabled={user && saveSearchLoading}
                >
                  {user && saveSearchLoading ? "Saving…" : saveSearchSuccess ? "Saved ✓" : "Save this search"}
                </button>
              </div>
              <div className='filter-group'>
                <label htmlFor='filter-category'>Category</label>
                <select
                  id='filter-category'
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value || "all"} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className='filter-group'>
                <label htmlFor='filter-type'>Type</label>
                <select
                  id='filter-type'
                  value={propertyType}
                  onChange={(e) => setPropertyType(e.target.value)}
                >
                  {propertyTypeOptions.map((opt) => (
                    <option key={opt.value || "all"} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className='filter-group'>
                <label htmlFor='filter-price-min'>Min price</label>
                <input
                  id='filter-price-min'
                  type='number'
                  min='0'
                  placeholder='e.g. 2000'
                  value={priceMin}
                  onChange={(e) => setPriceMin(e.target.value)}
                  className='listings-input'
                />
              </div>
              <div className='filter-group'>
                <label htmlFor='filter-price-max'>Max price</label>
                <input
                  id='filter-price-max'
                  type='number'
                  min='0'
                  placeholder='e.g. 10000'
                  value={priceMax}
                  onChange={(e) => setPriceMax(e.target.value)}
                  className='listings-input'
                />
              </div>
              <div className='filter-group'>
                <label htmlFor='filter-sort'>Sort</label>
                <select
                  id='filter-sort'
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {error && (
            <div className='listings-grid listings-grid--empty'>
              {error}
            </div>
          )}
          {!error && loading && (
            <div className='listings-grid listings-grid--empty'>
              Loading listings…
            </div>
          )}
          {!error && !loading && listings.length > 0 && (
            <div className='listings-grid'>
              {listings.map((item) => (
                <ListingCard
                  key={item.id}
                  listing={item}
                  isSaved={savedListingIds.includes(item.id)}
                  onToggleSave={handleToggleSaveListing}
                />
              ))}
            </div>
          )}
          {!error && !loading && listings.length === 0 && (
            <div className='listings-grid listings-grid--empty'>
              No properties match your filters. Try adjusting category, type, or price range.
            </div>
          )}
        </div>
      </section>
      {saveSearchSuccess && (
        <p className='listings-save-search-toast' role='status'>
          Search saved. View it in <Link to='/dashboard/saved-searches'>Dashboard → Saved searches</Link>.
        </p>
      )}
      {showSignInPrompt && (
        <SignInPrompt message={signInPromptMessage} onClose={() => setShowSignInPrompt(false)} />
      )}
    </div>
  )
}

export default Listings
